import { useCallback, useEffect, useState } from 'react';
import { api, errorText, formatQty } from '../api';
import { useAuth } from '../auth';
import { Badge, Card, CheckField, Empty, Loading, Notice, TextField } from '../components/ui';
import type {
  Article,
  LineCondition,
  Location,
  Partner,
  ReturnLine,
  ReturnOrder,
  ReturnStatus,
  Warehouse,
} from '../types';

const STATUS_STYLE: Record<ReturnStatus, 'warn' | 'info' | 'ok' | 'default'> = {
  angekuendigt: 'warn',
  teilweise: 'info',
  abgeschlossen: 'ok',
  storniert: 'default',
};

const CONDITION_LABEL: Record<LineCondition, string> = {
  verkaufsfaehig: 'verkaufsfähig',
  sperrbestand: 'Sperrbestand',
  vernichtung: 'Vernichtung',
};

const CONDITION_STYLE: Record<LineCondition, 'ok' | 'warn' | 'bad'> = {
  verkaufsfaehig: 'ok',
  sperrbestand: 'warn',
  vernichtung: 'bad',
};

interface DraftLine {
  article: Article;
  qtyExpected: string;
  condition: LineCondition;
}

export function ReturnsPage() {
  const { owners, can } = useAuth();
  const mayCreate = can('admin', 'kaufmann', 'disponent');
  const mayReceive = can('admin', 'schichtleiter', 'lagerist', 'disponent');

  const [orders, setOrders] = useState<ReturnOrder[] | null>(null);
  const [openOnly, setOpenOnly] = useState(true);
  const [openId, setOpenId] = useState<string | null>(null);
  const [detail, setDetail] = useState<{ order: ReturnOrder; lines: ReturnLine[] } | null>(null);

  const [warehouses, setWarehouses] = useState<Warehouse[]>([]);
  const [customers, setCustomers] = useState<Partner[]>([]);
  const [locations, setLocations] = useState<Location[]>([]);

  const [showForm, setShowForm] = useState(false);
  const [draft, setDraft] = useState({ ownerId: '', customerId: '', warehouseId: '', reason: '' });
  const [draftLines, setDraftLines] = useState<DraftLine[]>([]);
  const [term, setTerm] = useState('');
  const [hits, setHits] = useState<Article[]>([]);

  const [receiveFor, setReceiveFor] = useState<string | null>(null);
  const [receive, setReceive] = useState({ qty: '', locationCode: '', lotCode: '' });

  const [error, setError] = useState('');
  const [message, setMessage] = useState('');
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    setError('');
    try {
      setOrders(
        await api<ReturnOrder[]>('/api/returns/orders', {
          query: { openOnly: openOnly ? 'true' : undefined },
        }),
      );
    } catch (err) {
      setError(errorText(err));
    }
  }, [openOnly]);

  useEffect(() => {
    void load();
  }, [load]);

  useEffect(() => {
    api<Warehouse[]>('/api/warehouse').then(setWarehouses).catch(() => setWarehouses([]));
    api<Partner[]>('/api/partners', { query: { role: 'customer' } })
      .then(setCustomers)
      .catch(() => setCustomers([]));
    api<Location[]>('/api/warehouse/locations').then(setLocations).catch(() => setLocations([]));
  }, []);

  useEffect(() => {
    if (!term.trim() || !draft.ownerId) {
      setHits([]);
      return;
    }
    const timer = setTimeout(() => {
      api<Article[]>('/api/articles/lookup', { query: { q: term.trim() } })
        .then((found) => setHits(found.filter((a) => a.owner_id === draft.ownerId)))
        .catch(() => setHits([]));
    }, 200);
    return () => clearTimeout(timer);
  }, [term, draft.ownerId]);

  const loadDetail = useCallback(async (id: string) => {
    setDetail(null);
    try {
      setDetail(await api<{ order: ReturnOrder; lines: ReturnLine[] }>(`/api/returns/orders/${id}`));
    } catch (err) {
      setError(errorText(err));
    }
  }, []);

  useEffect(() => {
    if (openId) void loadDetail(openId);
  }, [openId, loadDetail]);

  async function createOrder() {
    if (draftLines.length === 0) return setError('Mindestens eine Position wird gebraucht.');
    setBusy(true);
    setError('');
    try {
      const created = await api<ReturnOrder>('/api/returns/orders', {
        method: 'POST',
        body: {
          ownerId: draft.ownerId,
          customerId: draft.customerId || null,
          warehouseId: draft.warehouseId,
          reason: draft.reason || null,
          lines: draftLines.map((line) => ({
            articleId: line.article.id,
            qtyExpected: Number(line.qtyExpected.replace(',', '.')),
            condition: line.condition,
          })),
        },
      });
      setMessage(`Retoure ${created.number} angelegt.`);
      setShowForm(false);
      setDraftLines([]);
      await load();
      setOpenId(created.id);
    } catch (err) {
      setError(errorText(err));
    } finally {
      setBusy(false);
    }
  }

  function startReceive(line: ReturnLine) {
    setReceiveFor(line.id);
    setReceive({
      qty: String(Math.max(0, line.qty_expected - line.qty_received)),
      locationCode: '',
      lotCode: '',
    });
  }

  async function confirmReceive(line: ReturnLine) {
    if (!detail) return;
    const qty = Number(receive.qty.replace(',', '.'));
    if (!Number.isFinite(qty) || qty <= 0) return setError('Bitte eine gültige Menge angeben.');

    let locationId: string | null = null;
    if (line.condition !== 'vernichtung') {
      const found = locations.find(
        (l) => l.code.toUpperCase() === receive.locationCode.trim().toUpperCase(),
      );
      if (!found) return setError(`Lagerplatz ${receive.locationCode} ist unbekannt.`);
      locationId = found.id;
    }

    setBusy(true);
    setError('');
    try {
      const result = await api<{ duplicate: boolean; stocked: boolean }>(
        `/api/returns/orders/${detail.order.id}/lines/${line.id}/receive`,
        {
          method: 'POST',
          body: {
            movementId: crypto.randomUUID(),
            qty,
            toLocationId: locationId,
            lotCode: receive.lotCode || null,
          },
        },
      );
      setMessage(
        result.duplicate
          ? 'Diese Annahme war bereits erfasst.'
          : result.stocked
            ? `${formatQty(qty, line.unit)} zurück in den Bestand genommen.`
            : `${formatQty(qty, line.unit)} zur Vernichtung erfasst — nicht eingelagert.`,
      );
      setReceiveFor(null);
      await loadDetail(detail.order.id);
      await load();
    } catch (err) {
      setError(errorText(err));
    } finally {
      setBusy(false);
    }
  }

  return (
    <>
      {error && <Notice kind="error">{error}</Notice>}
      {message && <Notice kind="success">{message}</Notice>}

      <div className="toolbar">
        <CheckField label="Nur offene Retouren" checked={openOnly} onChange={setOpenOnly} />
        <span className="spacer" />
        {mayCreate && (
          <button className="primary" onClick={() => setShowForm(!showForm)}>
            {showForm ? 'Abbrechen' : 'Neue Retoure'}
          </button>
        )}
      </div>

      {showForm && (
        <Card title="Retoure anlegen">
          <div className="form-grid">
            <label className="field">
              <span>Eigentümer</span>
              <select value={draft.ownerId} onChange={(e) => setDraft({ ...draft, ownerId: e.target.value })}>
                <option value="">— bitte wählen —</option>
                {owners.map((owner) => (
                  <option key={owner.id} value={owner.id}>
                    {owner.name}
                  </option>
                ))}
              </select>
            </label>
            <label className="field">
              <span>Kunde (optional)</span>
              <select
                value={draft.customerId}
                onChange={(e) => setDraft({ ...draft, customerId: e.target.value })}
              >
                <option value="">— keiner —</option>
                {customers.map((customer) => (
                  <option key={customer.id} value={customer.id}>
                    {customer.name}
                  </option>
                ))}
              </select>
            </label>
            <label className="field">
              <span>Lager</span>
              <select
                value={draft.warehouseId}
                onChange={(e) => setDraft({ ...draft, warehouseId: e.target.value })}
              >
                <option value="">— bitte wählen —</option>
                {warehouses.map((warehouse) => (
                  <option key={warehouse.id} value={warehouse.id}>
                    {warehouse.code} · {warehouse.name}
                  </option>
                ))}
              </select>
            </label>
            <TextField
              label="Grund"
              value={draft.reason}
              onChange={(v) => setDraft({ ...draft, reason: v })}
              hint="z. B. Transportschaden, Fehllieferung"
            />
          </div>

          {draft.ownerId && (
            <>
              <label className="field">
                <span>Artikel suchen</span>
                <input value={term} onChange={(e) => setTerm(e.target.value)} placeholder="Artikelnummer oder Name" />
              </label>
              {hits.length > 0 && (
                <div className="pick-list">
                  {hits.map((hit) => (
                    <button
                      key={hit.id}
                      onClick={() => {
                        setDraftLines((c) => [
                          ...c,
                          { article: hit, qtyExpected: '', condition: 'verkaufsfaehig' },
                        ]);
                        setTerm('');
                        setHits([]);
                      }}
                    >
                      <strong>{hit.name}</strong>
                      <div className="small muted mono">{hit.sku}</div>
                    </button>
                  ))}
                </div>
              )}
            </>
          )}

          {draftLines.length > 0 && (
            <table style={{ marginTop: 10 }}>
              <thead>
                <tr>
                  <th>Pos</th>
                  <th>Artikel</th>
                  <th className="num">Menge</th>
                  <th>Zustand</th>
                  <th />
                </tr>
              </thead>
              <tbody>
                {draftLines.map((line, index) => (
                  <tr key={`${line.article.id}-${index}`}>
                    <td>{index + 1}</td>
                    <td>
                      <span className="mono">{line.article.sku}</span> {line.article.name}
                    </td>
                    <td className="num" style={{ width: 120 }}>
                      <input
                        style={{ textAlign: 'right' }}
                        value={line.qtyExpected}
                        onChange={(e) =>
                          setDraftLines((c) =>
                            c.map((entry, i) => (i === index ? { ...entry, qtyExpected: e.target.value } : entry)),
                          )
                        }
                      />
                    </td>
                    <td style={{ width: 170 }}>
                      <select
                        value={line.condition}
                        onChange={(e) =>
                          setDraftLines((c) =>
                            c.map((entry, i) =>
                              i === index ? { ...entry, condition: e.target.value as LineCondition } : entry,
                            ),
                          )
                        }
                      >
                        {(Object.keys(CONDITION_LABEL) as LineCondition[]).map((condition) => (
                          <option key={condition} value={condition}>
                            {CONDITION_LABEL[condition]}
                          </option>
                        ))}
                      </select>
                    </td>
                    <td className="num">
                      <button className="small ghost" onClick={() => setDraftLines((c) => c.filter((_, i) => i !== index))}>
                        entfernen
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}

          <div style={{ display: 'flex', gap: 8, marginTop: 12 }}>
            <button className="primary" onClick={createOrder} disabled={busy}>
              {busy ? 'Anlegen …' : 'Retoure anlegen'}
            </button>
            <button onClick={() => setShowForm(false)}>Abbrechen</button>
          </div>
        </Card>
      )}

      <Card title="Retouren" padded={false}>
        {!orders ? (
          <Loading />
        ) : orders.length === 0 ? (
          <Empty>
            {openOnly ? (
              <>
                Keine offenen Retouren.{' '}
                <button className="ghost small" onClick={() => setOpenOnly(false)}>
                  Auch abgeschlossene zeigen
                </button>
              </>
            ) : (
              'Keine Retouren vorhanden.'
            )}
          </Empty>
        ) : (
          <table>
            <thead>
              <tr>
                <th>Nummer</th>
                <th>Eigentümer</th>
                <th>Kunde</th>
                <th>Grund</th>
                <th>Status</th>
                <th className="num">Angenommen</th>
                <th />
              </tr>
            </thead>
            <tbody>
              {orders.map((order) => (
                <tr key={order.id}>
                  <td className="mono">{order.number}</td>
                  <td className="small">{order.owner_name}</td>
                  <td className="small">{order.customer_name ?? '—'}</td>
                  <td className="small">{order.reason ?? '—'}</td>
                  <td>
                    <Badge kind={STATUS_STYLE[order.status]}>{order.status}</Badge>
                  </td>
                  <td className="num small">
                    {formatQty(order.qty_received)} / {formatQty(order.qty_expected)}
                  </td>
                  <td className="num">
                    <button className="small ghost" onClick={() => setOpenId(order.id)}>
                      öffnen
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </Card>

      {openId && (
        <Card
          title={detail ? `Retoure ${detail.order.number}` : 'Retoure'}
          actions={
            <button className="small" onClick={() => setOpenId(null)}>
              schliessen
            </button>
          }
          padded={false}
        >
          {!detail ? (
            <Loading />
          ) : (
            <>
              <table>
                <thead>
                  <tr>
                    <th>Pos</th>
                    <th>Artikel</th>
                    <th>Zustand</th>
                    <th className="num">Erwartet</th>
                    <th className="num">Angenommen</th>
                    <th />
                  </tr>
                </thead>
                <tbody>
                  {detail.lines.map((line) => (
                    <tr key={line.id}>
                      <td>{line.position}</td>
                      <td>
                        <span className="mono">{line.sku}</span> {line.article_name}
                      </td>
                      <td>
                        <Badge kind={CONDITION_STYLE[line.condition]}>
                          {CONDITION_LABEL[line.condition]}
                        </Badge>
                      </td>
                      <td className="num">{formatQty(line.qty_expected, line.unit)}</td>
                      <td className="num">{formatQty(line.qty_received)}</td>
                      <td className="num">
                        {mayReceive &&
                          detail.order.status !== 'abgeschlossen' &&
                          detail.order.status !== 'storniert' && (
                            <button className="small ghost" onClick={() => startReceive(line)}>
                              annehmen
                            </button>
                          )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>

              {receiveFor && (
                <div className="body" style={{ borderTop: '1px solid var(--line)', background: '#fafbfc' }}>
                  {(() => {
                    const line = detail.lines.find((l) => l.id === receiveFor);
                    if (!line) return null;
                    return (
                      <>
                        <strong>Position {line.position} annehmen</strong>
                        {line.condition === 'vernichtung' && (
                          <Notice kind="info">
                            Zu vernichtende Ware wird dokumentiert, aber nicht eingelagert — sie soll
                            gar nicht erst im verfügbaren Bestand auftauchen.
                          </Notice>
                        )}
                        {line.condition === 'sperrbestand' && (
                          <Notice kind="info">
                            Gesperrte Ware gehört auf einen Platz vom Typ Sperrlager.
                          </Notice>
                        )}
                        <div className="form-grid" style={{ marginTop: 10 }}>
                          <TextField
                            label={`Menge (${line.unit})`}
                            value={receive.qty}
                            onChange={(v) => setReceive({ ...receive, qty: v })}
                          />
                          {line.condition !== 'vernichtung' && (
                            <label className="field">
                              <span>Ziel-Lagerplatz</span>
                              <input
                                list="location-codes-returns"
                                value={receive.locationCode}
                                onChange={(e) =>
                                  setReceive({ ...receive, locationCode: e.target.value.toUpperCase() })
                                }
                              />
                              <datalist id="location-codes-returns">
                                {locations.map((location) => (
                                  <option key={location.id} value={location.code} />
                                ))}
                              </datalist>
                            </label>
                          )}
                          {line.requires_lot && line.condition !== 'vernichtung' && (
                            <TextField
                              label="Charge"
                              value={receive.lotCode}
                              onChange={(v) => setReceive({ ...receive, lotCode: v })}
                            />
                          )}
                        </div>
                        <div style={{ display: 'flex', gap: 8 }}>
                          <button className="primary" onClick={() => confirmReceive(line)} disabled={busy}>
                            {busy ? 'Buchen …' : 'Annehmen'}
                          </button>
                          <button onClick={() => setReceiveFor(null)}>Abbrechen</button>
                        </div>
                      </>
                    );
                  })()}
                </div>
              )}
            </>
          )}
        </Card>
      )}
    </>
  );
}
